import { z } from 'zod';
import { SIGNIFICANT_DATE_TYPES } from './significant-dates.ts';
// Value import (not `import type`) — `GENERIC_DRAFT_RELATIONSHIPS` spreads it
// at runtime so the case-less drafter's relationship list can never drift from
// the one a real support pathway is created with. Safe: `cases.ts` imports
// only types from its siblings and nothing at all from this module, so there
// is no cycle.
import { DECEASED_RELATIONSHIPS } from './cases.ts';

// =============================================================================
// Multi-classifier safety pipeline contract — DESIGN §5.3.1.
//
// `MaeveResponseSchema` is the structured-output contract enforced via Vertex
// AI's `response_schema` parameter. Every Employee Companion turn returns the
// per-category labels, the aggregate, and the conversational `response` in a
// single call. The category set is *candidate* per DESIGN §5.3.1 and is gated
// on CGC sign-off before any row is loaded as active — adding or removing a
// category is a CGC governance decision, not a code-review one.
//
// The Zod schema is data-driven from `CLASSIFIER_CATEGORIES` so the wire
// schema, the TS types, and downstream incident-log writes can't drift from
// one another.
// =============================================================================

// Per-category traffic light — DESIGN §5.3.1. `absent` is "no signal seen",
// not "green"; aggregate `green` is the absence of every category amber/red.
export const ClassifierLevel = z.enum(['absent', 'amber', 'red']);
export type ClassifierLevel = z.infer<typeof ClassifierLevel>;

// Aggregate session-level light — DESIGN §1.4, §5.3.1.
export const TrafficLight = z.enum(['green', 'amber', 'red']);
export type TrafficLight = z.infer<typeof TrafficLight>;

// The eight CGC-candidate categories — DESIGN §5.3.1. Order is preserved for
// deterministic Vertex `response_schema` emission. Adding/removing a category
// requires CGC sign-off (clinical categories: MHP + GP/psychiatrist;
// `safety_concerns`: legal counsel).
export const CLASSIFIER_CATEGORIES = [
  'suicidal_ideation',
  'self_harm_intent',
  'severe_isolation',
  'functional_impairment',
  'prolonged_grief_indicators',
  'substance_use_risk',
  'suicide_bereavement_disclosure',
  'safety_concerns',
] as const;
export type ClassifierCategory = (typeof CLASSIFIER_CATEGORIES)[number];

// Per-category map: `{ [category]: ClassifierLevel }`. Built dynamically so
// the schema can't drift from `CLASSIFIER_CATEGORIES`.
export const ClassifierMapSchema = z.object(
  Object.fromEntries(CLASSIFIER_CATEGORIES.map((cat) => [cat, ClassifierLevel])) as {
    [K in ClassifierCategory]: typeof ClassifierLevel;
  },
);
export type ClassifierMap = z.infer<typeof ClassifierMapSchema>;

// Employee-recorded significant dates (packages/shared/src/significant-dates.ts)
// — conversational detection. Co-generated with the reply, same single Vertex
// call, same posture as the classifiers: this is server-internal signal, never
// returned to the client (see `respondToEmployeeCompanion`). Capped at one
// entry per turn (mentioning several at once would be Maeve reciting a list,
// not attending to the person) — a caller that receives more just takes [0].
export const SignificantDateMentionSchema = z.object({
  dateType: z.enum(SIGNIFICANT_DATE_TYPES),
  label: z.string().min(1).max(120),
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(31),
});
export type SignificantDateMention = z.infer<typeof SignificantDateMentionSchema>;

// The two Companion sub-modes — DESIGN §10.1.3 "Sub-mode detection" and
// Instructions 1-2. `companion` is the MI/OARS reflective sub-mode; `practical`
// is the factual leave/RTW/touch-point-schedule answer drawn from
// `[Business_Scoped_RAG_Policy]`.
//
// NOTE: this is a *reporting* field, not a control field. Nothing in the
// pipeline branches on it — it exists so the split between the two sub-modes is
// measurable. PO observation (2026-08-01): Maeve reads as asking-and-consuming
// rather than offering, and the suspected cause is that sub-mode B is
// triple-gated (the employee must ASK, the answer must come strictly from
// policy text, and most orgs have no Approved + rules-cleared policy — see the
// `## Business_Scoped_RAG_Policy` fallback in `composeCompanionSystemInstruction`).
// Whether `practical` is ever entered at all is currently unknowable; this
// makes it observable BEFORE any clinical prompt copy is changed, since
// amending §10.1.3 Instructions 1/9 needs joint MHP + GP/psychiatrist re-sign-off.
export const COMPANION_SUB_MODES = ['companion', 'practical'] as const;
export type CompanionSubMode = (typeof COMPANION_SUB_MODES)[number];

// Structured output enforced via Vertex `response_schema` — DESIGN §5.3.1.
export const MaeveResponseSchema = z.object({
  classifiers: ClassifierMapSchema,
  aggregate_classification: TrafficLight,
  response: z.string().min(1),
  significant_date_mentions: z.array(SignificantDateMentionSchema).max(1).optional(),
  // Optional, NOT required — see `COMPANION_SUB_MODES`. This schema is shared
  // with the async `Trajectory_Classifier` call (`queue/trajectory.ts`), which
  // has no sub-mode concept at all; making it required would force that call to
  // emit a meaningless value. An omitted field is itself signal (logged as
  // `unreported`), so absence is recorded rather than silently defaulted.
  sub_mode: z.enum(COMPANION_SUB_MODES).optional(),
});
export type MaeveResponse = z.infer<typeof MaeveResponseSchema>;

// Gemini-compatible JSON schema for the `responseSchema` field in
// `generationConfig`. Vertex's `responseSchema` is an OpenAPI 3.0 subset, so
// we build it as a plain TS const (Zod-to-JSON-Schema would over-generate).
// Data-driven from `CLASSIFIER_CATEGORIES` so it can't drift from the Zod
// shape — the API test cross-checks the two at the property-set level.
export const MAEVE_VERTEX_RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    classifiers: {
      type: 'object',
      properties: Object.fromEntries(
        CLASSIFIER_CATEGORIES.map((cat) => [
          cat,
          { type: 'string', enum: ['absent', 'amber', 'red'] },
        ]),
      ),
      required: [...CLASSIFIER_CATEGORIES],
    },
    aggregate_classification: { type: 'string', enum: ['green', 'amber', 'red'] },
    response: { type: 'string' },
    // Optional — see `SignificantDateMentionSchema`. Not in `required`: most
    // turns have nothing to report here.
    significant_date_mentions: {
      type: 'array',
      maxItems: 1,
      items: {
        type: 'object',
        properties: {
          dateType: { type: 'string', enum: [...SIGNIFICANT_DATE_TYPES] },
          label: { type: 'string' },
          month: { type: 'integer' },
          day: { type: 'integer' },
        },
        required: ['dateType', 'label', 'month', 'day'],
      },
    },
    // Optional — see `COMPANION_SUB_MODES`. Deliberately absent from `required`
    // so the shared `Trajectory_Classifier` call isn't forced to emit it.
    sub_mode: { type: 'string', enum: [...COMPANION_SUB_MODES] },
  },
  required: ['classifiers', 'aggregate_classification', 'response'],
} as const;

export const ChatMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

// =============================================================================
// Manager Sub-mode A — first-response drafting. DESIGN §5.6.2, §10.1.2 #1.
//
// Three tone variants generated in a single Vertex call (NOT one at a time
// after a tone-preference question — per DESIGN, all three return in parallel
// so the Manager picks and edits). Maeve never sends the message; the Manager
// always sends.
// =============================================================================

export const TONE_VARIANTS = ['warm', 'formal', 'gentle'] as const;
export type ToneVariant = (typeof TONE_VARIANTS)[number];

// One variant of a manager first-response draft.
export const FirstResponseVariantSchema = z.object({
  tone: z.enum(TONE_VARIANTS),
  text: z.string().min(1),
});
export type FirstResponseVariant = z.infer<typeof FirstResponseVariantSchema>;

// `variants` must contain exactly one entry per tone. The .refine() catches the
// case where Gemini returns three "warm" variants — the API-side check
// enforces the contract regardless of model behaviour.
export const FirstResponseDraftSchema = z
  .object({
    variants: z.array(FirstResponseVariantSchema).length(TONE_VARIANTS.length),
  })
  .refine(
    (d) => {
      const tones = new Set(d.variants.map((v) => v.tone));
      return tones.size === TONE_VARIANTS.length;
    },
    { message: 'variants must include one entry per tone (warm/formal/gentle)' },
  );
export type FirstResponseDraft = z.infer<typeof FirstResponseDraftSchema>;

// Gemini-compatible response schema for `generationConfig.responseSchema`.
// Mirrors `FirstResponseDraftSchema` but written in the OpenAPI 3.0 subset
// Vertex accepts. Min/maxItems is enforced server-side by Gemini; the per-tone
// invariant is enforced by the Zod `.refine` above on the parsed payload.
export const FIRST_RESPONSE_VERTEX_SCHEMA = {
  type: 'object',
  properties: {
    variants: {
      type: 'array',
      minItems: TONE_VARIANTS.length,
      maxItems: TONE_VARIANTS.length,
      items: {
        type: 'object',
        properties: {
          tone: { type: 'string', enum: [...TONE_VARIANTS] },
          text: { type: 'string' },
        },
        required: ['tone', 'text'],
      },
    },
  },
  required: ['variants'],
} as const;

// Body for `POST /api/cases/:id/maeve/first-response/send` (M3 Sub-mode A
// auto-setter, DESIGN §6.4 / §9.3). The Manager picks one tone from the draft
// and confirms "I sent it" — the server stamps `first_response_sent_at` and
// short-circuits the §6.4 24h non-response Amber alert.
//
// `tone` is required and must be one of the canonical Sub-mode A tones
// (`warm` / `formal` / `gentle`) — guards against a client confirming with a
// bogus tone (e.g. one the prompt loader hadn't approved) and accidentally
// marking a case from a different draft surface. `text` is optional and is
// the Manager's final copy (their edits + the chosen tone's wording); the
// server does not persist it in this slice — the alert only cares that the
// response was sent, not what it said. The field is accepted (and bounded)
// for forward-compat: a future slice that logs the sent text to the audit
// trail can read it from this same body without a client-side breaking
// change. `text` is required to be non-empty if present so a future audit
// logger never writes a blank transcript.
export const SendFirstResponseRequestSchema = z.object({
  tone: z.enum(TONE_VARIANTS),
  text: z.string().min(1).max(4000).optional(),
});
export type SendFirstResponseRequest = z.infer<typeof SendFirstResponseRequestSchema>;

// =============================================================================
// Manager Sub-mode A′ — CASE-LESS generic response drafting (PO 2026-08-06).
//
// Sub-mode A above is the per-pathway first response: it is built from a
// trusted case row and audited against that case's `employeeId`. The PO's
// observation is that it assumes a pathway already exists, which a manager
// signing in for the first time — the exact moment they are most likely to
// need wording help — does not have. This sub-mode is the generic half:
// "help me word this", tagged to nobody.
//
// THE REQUEST BODY HAS NO FIELD THAT CAN CARRY A PERSON'S IDENTITY, and that
// is the whole safety design rather than a side effect. Both inputs are
// closed enums, so:
//   - there is no free text to sanitise before interpolation (contrast
//     `sanitiseInterpolationField`, which every case-bearing sub-mode needs),
//   - a manager cannot paste an employee's name, diagnosis or circumstances
//     into an un-audited surface even by accident, and
//   - "not tagged to anything" is structural, not a copy promise.
// Any future request to widen this to free text is a CGC/DPIA decision, not
// an engineering one.
//
// It rides the SAME CGC-signed-off `Manager_Guidance` prompt row as Sub-modes
// A–D, with the sub-mode tag carrying the structural contract (the Sub-mode D
// pattern — "the tag IS the contract"). No new `promptMode`, no new
// `system_prompts` row, no migration: the CGC gate is an expansion of the
// existing Manager sign-off rather than a fresh one.
//
// Response shape is deliberately `FirstResponseDraftSchema` — the same three
// tone variants Sub-mode A returns — so the tone-picker composer is literally
// the same component on both surfaces.
// =============================================================================

// What the manager needs wording for. Closed set: each maps to a fixed line
// in the user message, so the model never sees manager-authored prose.
export const GENERIC_DRAFT_SITUATIONS = [
  'first_response',
  'no_reply_yet',
  'practical_arrangements',
  'checking_in',
  'returning_to_work',
] as const;
export type GenericDraftSituation = (typeof GENERIC_DRAFT_SITUATIONS)[number];

// Who died, when the manager knows. `Unspecified` is first-class rather than
// an absent field: a manager who has heard "there's been a death in the
// family" and nothing more still needs wording, and forcing a guess here
// would put an invented fact into the draft.
export const GENERIC_DRAFT_RELATIONSHIPS = [...DECEASED_RELATIONSHIPS, 'Unspecified'] as const;
export type GenericDraftRelationship = (typeof GENERIC_DRAFT_RELATIONSHIPS)[number];

export const GenericDraftRequestSchema = z.object({
  situation: z.enum(GENERIC_DRAFT_SITUATIONS),
  relationship: z.enum(GENERIC_DRAFT_RELATIONSHIPS),
});
export type GenericDraftRequest = z.infer<typeof GenericDraftRequestSchema>;

// =============================================================================
// Manager Sub-mode B — guidance + policy citations. DESIGN §5.6.2, §10.1.2 #2.
//
// Multi-turn conversation. Manager asks a question; Maeve cites policy and
// ends with the manager's agency. Stateless on the server — the client owns
// the conversation history and POSTs it on each turn. The trust boundary:
// client-supplied history is untrusted but bounded by the system prompt's
// constraints (advisory-only output, no employment decisions). Cross-session
// persistence is intentionally deferred until the DO architecture for Manager
// mode is defined.
// =============================================================================

// Single user/assistant turn. Reused by future Companion / HR Maeve chat
// surfaces; the role enum matches Vertex/Gemini's expected message shape.
export const GuidanceTurnSchema = z.object({
  role: z.enum(['user', 'assistant']),
  text: z.string().min(1).max(4000),
});
export type GuidanceTurn = z.infer<typeof GuidanceTurnSchema>;

export const ManagerGuidanceRequestSchema = z.object({
  // The conversation so far, ending in the user's new question. Empty array
  // → no question yet; the route must 400. Last message must be user-role.
  messages: z.array(GuidanceTurnSchema).min(1).max(40),
});
export type ManagerGuidanceRequest = z.infer<typeof ManagerGuidanceRequestSchema>;

// Single-string response per DESIGN §10.1.2 #2 ("one recommendation at a
// time"). The model produces inline policy citations and the manager-agency
// suffix; we don't structure them out separately for v1. A future revision
// can split citations into structured metadata once the UI demands it.
export const ManagerGuidanceResponseSchema = z.object({
  message: z.string().min(1),
});
export type ManagerGuidanceResponse = z.infer<typeof ManagerGuidanceResponseSchema>;

export const GUIDANCE_VERTEX_SCHEMA = {
  type: 'object',
  properties: {
    message: { type: 'string' },
  },
  required: ['message'],
} as const;

// =============================================================================
// Manager Sub-mode C — touch point preparation. DESIGN §5.6.2, §10.1.2 #3.
//
// Multi-turn conversation. The manager opens the briefing for a specific
// upcoming touch point; Maeve returns 2–3 conversation pointers based on the
// case stage and touch point type. Follow-up turns let the manager ask
// clarifying questions ("why this topic?", "what if they push back?")
// without losing the briefing context. Stateless on the server; the
// client owns the history and POSTs it on every turn.
//
// The shape distinguishes Sub-mode C from B by the `touchpointId` field
// (required) and the structured `pointers` array in the response. The
// model emits `pointers` on every turn — initial briefing populates them,
// follow-up turns either carry them forward unchanged or update them if
// the manager asks for different pointers. The optional `message` field
// is the conversational reply text; on the initial briefing turn it's
// typically omitted (the pointers are the briefing), on follow-up turns
// it carries the answer to the manager's question.
//
// Per DESIGN §10.1.2 #3, the model MUST NOT script the conversation —
// `suggestedPhrasing` is an example opener the manager can adapt, NOT a
// line for them to read out verbatim. The system prompt is responsible
// for keeping the model on advisory ground; this schema just gives the
// output a stable rendering shape.
// =============================================================================

export const TouchpointPrepPointerSchema = z.object({
  topic: z.string().min(1).max(200),
  reasoning: z.string().min(1).max(1000),
  // Optional — the model may omit when no opener phrasing fits, or when
  // the manager has explicitly asked for the pointers without examples.
  suggestedPhrasing: z.string().min(1).max(500).optional(),
});
export type TouchpointPrepPointer = z.infer<typeof TouchpointPrepPointerSchema>;

export const TouchpointPrepRequestSchema = z.object({
  // The specific touch point the manager is preparing for. Route layer
  // verifies it belongs to the case (cross-case 404 via the manager-scoped
  // case lookup combined with the per-case touch point fetch).
  touchpointId: z.string().uuid(),
  messages: z.array(GuidanceTurnSchema).min(1).max(40),
});
export type TouchpointPrepRequest = z.infer<typeof TouchpointPrepRequestSchema>;

// `pointers` is mandatory and bounded 2–3 per §10.1.2 #3. `message` is
// optional; on the first turn it's typically absent (the pointers ARE the
// briefing), on follow-up turns the model uses it to answer the manager's
// question while leaving (or updating) the pointers.
export const TouchpointPrepResponseSchema = z.object({
  pointers: z.array(TouchpointPrepPointerSchema).min(2).max(3),
  message: z.string().min(1).optional(),
});
export type TouchpointPrepResponse = z.infer<typeof TouchpointPrepResponseSchema>;

export const TOUCHPOINT_PREP_VERTEX_SCHEMA = {
  type: 'object',
  properties: {
    pointers: {
      type: 'array',
      minItems: 2,
      maxItems: 3,
      items: {
        type: 'object',
        properties: {
          topic: { type: 'string' },
          reasoning: { type: 'string' },
          suggestedPhrasing: { type: 'string' },
        },
        required: ['topic', 'reasoning'],
      },
    },
    message: { type: 'string' },
  },
  required: ['pointers'],
} as const;

// =============================================================================
// HR touch-point guidance — Support Framework spec §15 ("HR Maeve flow").
//
// When HR opens a touch point, Maeve guides them through five oversight areas:
// what to confirm, whether the manager actioned it, whether flags/concerns
// exist, whether policy/EAP/occupational-health guidance applies, and whether
// the case should continue/pause/escalate. This is HR PROCESS + OVERSIGHT
// guidance, not grief counselling — so it rides the existing `HR_Maeve` prompt
// row (legal_counsel-signed) with a sub-mode tag, mirroring how the Manager
// touch-point prep rides `Manager_Guidance`. No new prompt mode / CGC gate; it
// inherits HR_Maeve's sign-off + §4.7 boilerplate.
// =============================================================================

export const TOUCHPOINT_HR_GUIDANCE_AREAS = [
  'confirm',
  'manager_action',
  'flags',
  'policy',
  'continuation',
] as const;
export type TouchpointHrGuidanceArea = (typeof TOUCHPOINT_HR_GUIDANCE_AREAS)[number];

export const TouchpointHrGuidanceCheckSchema = z.object({
  area: z.enum(TOUCHPOINT_HR_GUIDANCE_AREAS),
  guidance: z.string().min(1).max(1000),
});
export type TouchpointHrGuidanceCheck = z.infer<typeof TouchpointHrGuidanceCheckSchema>;

export const TouchpointHrGuidanceRequestSchema = z.object({
  touchpointId: z.string().uuid(),
  messages: z.array(GuidanceTurnSchema).min(1).max(40),
});
export type TouchpointHrGuidanceRequest = z.infer<typeof TouchpointHrGuidanceRequestSchema>;

// `checks` covers the §15 oversight areas (3–5 items; not every area applies to
// every touch point). `message` is an optional follow-up answer on later turns.
export const TouchpointHrGuidanceResponseSchema = z.object({
  checks: z.array(TouchpointHrGuidanceCheckSchema).min(3).max(5),
  message: z.string().min(1).optional(),
});
export type TouchpointHrGuidanceResponse = z.infer<typeof TouchpointHrGuidanceResponseSchema>;

export const TOUCHPOINT_HR_GUIDANCE_VERTEX_SCHEMA = {
  type: 'object',
  properties: {
    checks: {
      type: 'array',
      minItems: 3,
      maxItems: 5,
      items: {
        type: 'object',
        properties: {
          area: {
            type: 'string',
            enum: ['confirm', 'manager_action', 'flags', 'policy', 'continuation'],
          },
          guidance: { type: 'string' },
        },
        required: ['area', 'guidance'],
      },
    },
    message: { type: 'string' },
  },
  required: ['checks'],
} as const;

// =============================================================================
// Manager Sub-mode D — Return-to-Work plan fill-helper. DESIGN §6.5, §10.1.2.
//
// One-shot structured draft of the four Manager-submittable RTW plan fields,
// triggered when the Manager clicks "Suggest a draft from Maeve" in the
// case-detail RTW panel. Multi-turn (supports follow-up "why this phased
// return?" questions) but the *contract* is structured suggestions + per-field
// rationale on every turn, not free-form chat. The Manager picks per field
// via "Use this" buttons — Maeve never auto-fills the form.
//
// Rides the same `Manager_Guidance` system_prompts row as Sub-modes A/B/C.
// The sub-mode-D contract is currently carried by the system-instruction
// sub-mode tag composed server-side; once the CGC drafts dedicated Sub-mode
// D language inside the prompt itself, the tag will become a routing pin
// like the others. Until then the tag is the contract.
//
// `managerPrivateNotes` is deliberately NOT in the response shape — it is by
// definition the Manager's private context and must never be authored by
// Maeve.
// =============================================================================

// Phased-return values must match `PHASED_RETURN_VALUES` in `@besideu/shared/cases`.
// Duplicated here so this module doesn't depend on `cases.ts`; the test
// suite asserts the two stay in lockstep.
const PHASED_RETURN_VALUES_FOR_DRAFT = ['Full_Time', 'Part_Time', 'Staggered'] as const;

export const RtwPlanDraftRequestSchema = z.object({
  // The conversation so far, ending in the user's new question. Empty array
  // → no question yet; the route 400s. Last message must be user-role
  // (route-side check). Initial-turn payload is typically a single message
  // like `[{ role: 'user', text: 'Suggest a draft.' }]`.
  messages: z.array(GuidanceTurnSchema).min(1).max(40),
});
export type RtwPlanDraftRequest = z.infer<typeof RtwPlanDraftRequestSchema>;

// Suggestions mirror `SubmitRtwPlanRequest` (cases.ts) minus
// `managerPrivateNotes`. `targetFullLoadDate` is optional — Full_Time returns
// don't have a ramp, and the model may decline to commit to a date even on
// Part_Time / Staggered. The route does NOT validate cross-field consistency
// (e.g. Full_Time + targetFullLoadDate); the Manager's form already
// conditionally hides the date for Full_Time, so any stale model-suggested
// date is dropped at submit time anyway.
const RtwPlanDraftSuggestionsSchema = z.object({
  workloadExpectations: z.string().min(1).max(5_000),
  phasedReturn: z.enum(PHASED_RETURN_VALUES_FOR_DRAFT),
  targetFullLoadDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
    .optional(),
  supportPreferences: z.string().min(1).max(5_000),
});

// Per-field rationale. Each entry is one short sentence; the model is
// instructed to cite policy by filename inline in the rationale prose (same
// posture as Sub-mode B `ManagerGuidanceResponseSchema` — citations are
// inline strings, not structured metadata). The targetFullLoadDate rationale
// is optional because the suggestion itself is optional.
const RtwPlanDraftRationaleSchema = z.object({
  workloadExpectations: z.string().min(1).max(2_000),
  phasedReturn: z.string().min(1).max(2_000),
  targetFullLoadDate: z.string().min(1).max(2_000).optional(),
  supportPreferences: z.string().min(1).max(2_000),
});

export const RtwPlanDraftResponseSchema = z.object({
  suggestions: RtwPlanDraftSuggestionsSchema,
  rationale: RtwPlanDraftRationaleSchema,
  // Optional opening / follow-up sentence. On the initial turn the model
  // typically frames the suggestions ("Based on the bereavement type and your
  // policy excerpts…"); on follow-up turns it answers the Manager's specific
  // question and may carry forward unchanged suggestions.
  message: z.string().min(1).optional(),
});
export type RtwPlanDraftResponse = z.infer<typeof RtwPlanDraftResponseSchema>;

// Vertex `responseSchema` — OpenAPI 3.0 subset. Optional fields are signalled
// by omission from `required` (Gemini doesn't accept the `nullable` keyword
// reliably). Data-driven from `PHASED_RETURN_VALUES_FOR_DRAFT` so the wire
// schema can't drift from the Zod enum.
export const RTW_PLAN_DRAFT_VERTEX_SCHEMA = {
  type: 'object',
  properties: {
    suggestions: {
      type: 'object',
      properties: {
        workloadExpectations: { type: 'string' },
        phasedReturn: { type: 'string', enum: [...PHASED_RETURN_VALUES_FOR_DRAFT] },
        targetFullLoadDate: { type: 'string' },
        supportPreferences: { type: 'string' },
      },
      required: ['workloadExpectations', 'phasedReturn', 'supportPreferences'],
    },
    rationale: {
      type: 'object',
      properties: {
        workloadExpectations: { type: 'string' },
        phasedReturn: { type: 'string' },
        targetFullLoadDate: { type: 'string' },
        supportPreferences: { type: 'string' },
      },
      required: ['workloadExpectations', 'phasedReturn', 'supportPreferences'],
    },
    message: { type: 'string' },
  },
  required: ['suggestions', 'rationale'],
} as const;

// =============================================================================
// HR Maeve mode — DESIGN §5.6.1, §10.1.1.
//
// Structured information retrieval + case-management overlay for HR_Admin /
// HR_Viewer users. Not a conversational companion: no classifier pipeline, no
// DO session, no red-flag dispatch path. Multi-turn but stateless on the
// server — the client owns the history and POSTs it on each turn (same trust
// posture as Manager Sub-mode B). Output is plain text per §10.1.1 #7;
// `responseMimeType: 'text/plain'` so the model isn't forced through a
// JSON schema for what is structurally just prose.
//
// The injected context blocks (§10.1.1):
//   - `[Organization_Policy_Text]` — same R2 loader as Manager Sub-mode B
//   - `[Active_Cases_Summary]` — org-wide non-closed cases (HR has org-wide
//      visibility per §1.2). Capped at 50 most-recently-updated to bound the
//      system-instruction budget.
//   - `[Anonymised_Wellbeing_Aggregates]` — amber-aggregated incident-log
//      counts per category over the last 90 days, gated by §6.4 min-pool size.
//      Below-threshold categories are suppressed (never identified-by-elim).
//   - `[Named_HR_Users]` — org's active HR_Admin + HR_Viewer roster
//
// HR mode MUST NOT include: Employee Companion chat content, Manager guidance
// chat content, individual amber records below the pool threshold. Enforced
// behaviourally by what the service composes; tested at the route layer.
// =============================================================================

// Reuses `GuidanceTurnSchema` for the turn shape — HR and Manager modes share
// the same role/text envelope (Vertex/Gemini's contract).
export const HrMaeveQueryRequestSchema = z.object({
  messages: z.array(GuidanceTurnSchema).min(1).max(40),
});
export type HrMaeveQueryRequest = z.infer<typeof HrMaeveQueryRequestSchema>;

export const HrMaeveQueryResponseSchema = z.object({
  message: z.string().min(1),
});
export type HrMaeveQueryResponse = z.infer<typeof HrMaeveQueryResponseSchema>;

// =============================================================================
// Employee Companion turn — DESIGN §5.6.3, §5.3.1, §3.3, §3.3.4, §10.1.3.
//
// One request = one Maeve turn. Server keeps full session state in the
// per-employee Durable Object; the client never sees `sessionId`. The
// response surfaces the assistant `message`, the aggregate traffic light,
// and a `sessionEnded` flag (true when a red flag locked the session). The
// per-category classifier map is server-only metadata (DESIGN §3.3.3 /
// §8.4) and is never returned to the client.
// =============================================================================

export const EmployeeCompanionRequestSchema = z.object({
  text: z.string().min(1).max(4000),
});
export type EmployeeCompanionRequest = z.infer<typeof EmployeeCompanionRequestSchema>;

export const EmployeeCompanionResponseSchema = z.object({
  message: z.string().min(1),
  aggregate_classification: TrafficLight,
  /**
   * True when this turn triggered a red flag and the session has been locked.
   * The DO content has been wiped per Phase 1 (DESIGN §5.7.1); subsequent
   * turn requests will return `session_locked`.
   */
  sessionEnded: z.boolean(),
});
export type EmployeeCompanionResponse = z.infer<typeof EmployeeCompanionResponseSchema>;

// AI Disclosure surface — DESIGN §4.7, §9.3 #12, §10.1.4 (Slice 7).
// The client echoes back the `disclosurePromptId` it was shown so the server
// can detect a rotation between GET and POST (returns `version_mismatch` →
// 409, frontend re-fetches).
export const DisclosureAckRequestSchema = z.object({
  disclosurePromptId: z.string().uuid(),
});
export type DisclosureAckRequest = z.infer<typeof DisclosureAckRequestSchema>;

export const DisclosureStatusResponseSchema = z.object({
  promptText: z.string().min(1),
  promptVersionId: z.string().uuid(),
  version: z.string().min(1),
  needsAcknowledgement: z.boolean(),
  // QA sampler redactor slice — drives the Companion-mode opt-in toggle
  // surface. `qaConsentVisible` is the server's view of
  // `QA_OPT_IN_USER_VISIBLE === 'true'`: when false the toggle is hidden
  // entirely (no opt-in possible in this env). `qaConsentCurrent` is the
  // current opt-in state for the user's open Companion session (defaults
  // to false when the user has no open session yet — the toggle hydrates
  // to "off" until they start one and flip it). Both are derived
  // server-side so the client never has to read env vars.
  //
  // `qaConsentLookupFailed` reports whether the DO + D1 lookup that
  // computes `qaConsentCurrent` actually succeeded. We cannot fail-OPEN
  // here by defaulting `qaConsentCurrent` to false, because the server-
  // side snapshot decision at `endEmployeeCompanionSession` reads
  // `qa_session_consent` directly — so a UI that shows "off" while the
  // DB row says "on" silently inverts the user's privacy expectation.
  // When this flag is true, the SPA MUST surface a "couldn't verify
  // consent state" indicator instead of trusting `qaConsentCurrent`.
  qaConsentVisible: z.boolean(),
  qaConsentCurrent: z.boolean(),
  qaConsentLookupFailed: z.boolean(),
});
export type DisclosureStatusResponse = z.infer<typeof DisclosureStatusResponseSchema>;

export const DisclosureAckResponseSchema = z.object({
  acknowledgedAt: z.string(),
  promptVersionId: z.string().uuid(),
});
export type DisclosureAckResponse = z.infer<typeof DisclosureAckResponseSchema>;

// =============================================================================
// One-way upgrade ratchet — DESIGN §4.7, §5.3.1, §5.6.3 #8.
//
// Within a session, classifications can only escalate at both the category
// and aggregate level: `absent → amber → red`, `green → amber → red`. A red
// flag fired by message N cannot be downgraded by message N+1 even if the
// user reframes benignly. This is the specific defence against the
// false-reason-override failure mode (Amaurie ChatGPT case).
//
// Helpers are pure functions over state — the Worker calls them every turn
// with `(prevSessionState, newClassifierOutput)` before storing/routing.
// =============================================================================

const LEVEL_ORDER: Record<ClassifierLevel, number> = { absent: 0, amber: 1, red: 2 };
const AGGREGATE_ORDER: Record<TrafficLight, number> = { green: 0, amber: 1, red: 2 };

export function escalateLevel(prev: ClassifierLevel, next: ClassifierLevel): ClassifierLevel {
  return LEVEL_ORDER[next] > LEVEL_ORDER[prev] ? next : prev;
}

export function escalateAggregate(prev: TrafficLight, next: TrafficLight): TrafficLight {
  return AGGREGATE_ORDER[next] > AGGREGATE_ORDER[prev] ? next : prev;
}

export function mergeClassifiers(prev: ClassifierMap, next: ClassifierMap): ClassifierMap {
  const out = {} as ClassifierMap;
  for (const cat of CLASSIFIER_CATEGORIES) {
    out[cat] = escalateLevel(prev[cat], next[cat]);
  }
  return out;
}

export function emptyClassifierMap(): ClassifierMap {
  return Object.fromEntries(CLASSIFIER_CATEGORIES.map((c) => [c, 'absent'])) as ClassifierMap;
}

// =============================================================================
// Required boilerplate — DESIGN §4.7.1, §4.7.2, §4.7.3.
//
// Every Maeve-user-mode system prompt (HR_Maeve, Manager_Guidance,
// Employee_Companion) must include the Intended Use Statement verbatim, must
// include the Human Oversight Clause verbatim, and must reproduce or
// explicitly reference the Prohibited Use Cases. The §5.5 loader refuses to
// activate or serve a row missing any of the three.
//
// These constants are the source of truth in code. Any DESIGN.md / Safety
// Guide wording change MUST flow through this file — that's the feature, not
// the bug. The wording is the platform's primary legal protection.
// =============================================================================

export const INTENDED_USE_STATEMENT =
  'Maeve is a workplace bereavement support assistant designed to help employees, managers, and HR teams navigate bereavement-related workplace processes and supportive communication. Maeve is not a therapist, counsellor, mental health service, medical device, crisis service, or emergency response system. Maeve is not a substitute for professional clinical support, human relationships, or emergency services. All outputs generated by Maeve are advisory. They must remain subject to human judgement and organisational oversight at all times.';

export const HUMAN_OVERSIGHT_CLAUSE =
  'All outputs generated by Maeve across all three modes — HR, manager, and employee — are advisory only. besideU does not accept responsibility for decisions made solely on the basis of Maeve’s outputs without the application of human judgement. Organisations deploying besideU accept responsibility for ensuring appropriate human oversight is maintained.';

// At least one of these anchor phrases must appear in the body — proves the
// prompt either reproduces the list or explicitly references it. The §5.5
// loader treats this as a "reproduced or explicitly referenced" gate, not a
// verbatim full-list gate (the bullets themselves are paraphrasable in-line).
export const PROHIBITED_USE_CASES_ANCHORS: readonly string[] = [
  'Prohibited Use Cases',
  '§4.7.2',
  'Diagnose or assess any mental health condition',
];

// The three user-facing Maeve modes — DESIGN §5.5, §10.1. Boilerplate
// validation applies only to these; Policy_Audit, Trajectory_Classifier, and
// AI_Disclosure have approval-gates but no boilerplate gate.
export const MAEVE_USER_MODES = ['HR_Maeve', 'Manager_Guidance', 'Employee_Companion'] as const;
export type MaeveUserMode = (typeof MAEVE_USER_MODES)[number];

export function isMaeveUserMode(mode: string): mode is MaeveUserMode {
  return (MAEVE_USER_MODES as readonly string[]).includes(mode);
}

// =============================================================================
// CGC governance — DESIGN §5.5, §9.3 (Slice 12).
//
// Per-mode approval requirements: which adviser kind(s) may approve a
// `system_prompts` row, and whether joint sign-off is required. Single source
// of truth consumed by:
//   - the CGC governance repository (authorisation check on every approve)
//   - the system-prompts loader (refuses to serve unapproved / partially-
//     jointly-approved rows)
//   - the frontend CGC dashboard (filters the prompt list to modes the
//     logged-in member can act on)
//
// `requiredKinds` is the SET from which approvers must come. `jointSignOff:
// true` means TWO approvers from DIFFERENT required kinds must both sign off
// before the loader will serve the row. Per DESIGN §5.5:
//   - HR_Maeve / AI_Disclosure / Policy_Audit → legal counsel (single)
//   - Manager_Guidance → BACP-registered grief counsellor (single)
//   - Employee_Companion / Trajectory_Classifier → MHP + GP/psychiatrist
//     (joint — both kinds must sign off)
//   - Companion_Wrap_Nudge / Companion_Empathetic_Close → same as Companion
//     (joint MHP + GP/psychiatrist — they are sub-templates served alongside
//     the main Companion prompt and inherit its sign-off posture)
// =============================================================================

export const CGC_MEMBER_KINDS = [
  'grief_counsellor',
  'mhp',
  'gp_psychiatrist',
  'legal_counsel',
  'product',
] as const;
export type CgcMemberKind = (typeof CGC_MEMBER_KINDS)[number];

export function isCgcMemberKind(s: string): s is CgcMemberKind {
  return (CGC_MEMBER_KINDS as readonly string[]).includes(s);
}

export interface PromptApprovalRequirement {
  /** Adviser kinds permitted to approve. For joint modes, both must sign off. */
  readonly requiredKinds: readonly CgcMemberKind[];
  /** True when two DIFFERENT kinds in `requiredKinds` must both sign off. */
  readonly jointSignOff: boolean;
}

// `as const satisfies Record<string, PromptApprovalRequirement>` shape:
// - `satisfies` makes each value conform to PromptApprovalRequirement at
//   declaration time (so a malformed entry fails compile here).
// - `as const` narrows the inferred type, so `keyof typeof X` (used by
//   `PromptApprovalMode` below) reads the ACTUAL OBJECT KEYS — not a
//   restated key union from a type annotation. This eliminates the
//   structural drift where adding an object entry without updating a
//   parallel key annotation would silently bypass the
//   `Equals<PromptApprovalMode, …>` exhaustiveness check on
//   `PROMPT_DRAFT_MODES`. Now both checks read from the same source of
//   truth — the literal object keys here.
export const PROMPT_MODE_APPROVAL_REQUIREMENTS = {
  HR_Maeve: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  Manager_Guidance: { requiredKinds: ['grief_counsellor'], jointSignOff: false },
  Employee_Companion: {
    requiredKinds: ['mhp', 'gp_psychiatrist'],
    jointSignOff: true,
  },
  Policy_Audit: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  // Multi-Agent Policy Audit (DESIGN §5.4) — three sibling sub-prompts. We
  // mirror the Companion_Wrap_Nudge / Companion_Empathetic_Close precedent
  // (separate enum entries per agent role) so each can be independently
  // versioned and signed off by legal counsel. The legacy `Policy_Audit`
  // entry is retained for backwards compatibility with the pre-Slice-19
  // single-mode signature; the audit pipeline reads only the three
  // sub-modes below.
  Policy_Audit_Compliance: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  Policy_Audit_Empathy: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  Policy_Audit_Supervisor: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  Trajectory_Classifier: {
    requiredKinds: ['mhp', 'gp_psychiatrist'],
    jointSignOff: true,
  },
  AI_Disclosure: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  Companion_Wrap_Nudge: {
    requiredKinds: ['mhp', 'gp_psychiatrist'],
    jointSignOff: true,
  },
  Companion_Empathetic_Close: {
    requiredKinds: ['mhp', 'gp_psychiatrist'],
    jointSignOff: true,
  },
  // Slice 27 — boilerplate blocks. Legal-text changes; single legal_counsel
  // sign-off matches the wider legal-text governance posture (HR_Maeve,
  // AI_Disclosure, Policy_Audit_* all use the same shape).
  Boilerplate_Intended_Use: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  Boilerplate_Human_Oversight: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  Boilerplate_Prohibited_Use: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  // Slice 31 — DPA. Click-through legal contract; same single legal_counsel
  // sign-off posture as the rest of the legal-text governance set.
  DPA: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  // DPIA (PROJECT §5 hard gate #3.1) — Data Protection Impact Assessment.
  // Modelled as a versioned `system_prompts` row so legal counsel signs it
  // off through the standard CGC approve + activate workflow (audited via
  // `system_prompt_decisions` / `system_prompt_activations`), exactly like
  // DPA. Single legal_counsel sign-off; not a Maeve user mode, so no §4.7
  // boilerplate gate applies. Surfaced read-only in the admin Governance
  // panel alongside the DPA.
  DPIA: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  // QA sampler foundation slice — LLM-as-judge prompt. Joint sign-off:
  // MHP + GP/psychiatrist (clinical authority over the judge methodology
  // — the rubric is theirs). Same shape as Trajectory_Classifier and
  // Companion_*: the system_prompts schema has two approver columns
  // (approvedBy + approvedJointlyBy) so a "third required kind" is
  // structurally unrepresentable. Legal-counsel oversight of the
  // QA programme's exposure attaches via the DPA + AI_Disclosure
  // modes (both already legal_counsel-gated), which document the
  // automated-scoring + consent posture that the QA_Judge prompt
  // implements at the model level.
  QA_Judge: {
    requiredKinds: ['mhp', 'gp_psychiatrist'],
    jointSignOff: true,
  },
  // QA sampler redactor slice — LLM-as-redactor prompt. Single legal_counsel
  // sign-off matches the wider legal-text governance posture (DPA,
  // AI_Disclosure, Boilerplate_* all use the same shape). Redaction is a
  // data-handling concern: "what counts as identifying" is squarely a
  // legal-counsel call, and the clinical-context layer is preserved by the
  // QA_Judge programme which sits alongside. Adding a clinical kind here
  // would dilute the legal accountability without adding clinical value
  // (the redactor does not opine on bereavement content; it only strips
  // identifiers). Same single-approver shape as DPA / AI_Disclosure so
  // a future legal-counsel rotation cascades cleanly via the existing
  // CGC sign-off workflow.
  QA_Redactor: { requiredKinds: ['legal_counsel'], jointSignOff: false },
  // PROJECT §4 Step 2 — key-rule extraction prompt. Single legal_counsel
  // sign-off matches the wider legal-text governance posture (Policy_Audit_*,
  // DPA, AI_Disclosure, Boilerplate_*, QA_Redactor all use the same shape):
  // the extractor turns policy text into structured rules for HR review,
  // and the rules' legal accuracy is the legal-counsel concern. Clinical
  // sign-off (grief counsellor) is the natural second kind for bereavement-
  // specific tone, but PROJECT §4 keeps the V1 extraction surface
  // legal-only and adds grief-counsellor review as a V1.1 once the V1
  // extraction quality is measurable in pilot.
  Policy_KeyRule_Extraction: { requiredKinds: ['legal_counsel'], jointSignOff: false },
} as const satisfies Record<string, PromptApprovalRequirement>;

// Union of every prompt-mode key in `PROMPT_MODE_APPROVAL_REQUIREMENTS`.
// Mirrors `(typeof promptMode)[number]` from `@besideu/db` but lives at
// the shared layer so consumers don't need to depend on db. Used by
// `@besideu/shared/admin`'s PROMPT_DRAFT_MODES satisfies clause to
// compile-time-enforce that the drafting list is a subset of the
// authoritative mode set.
export type PromptApprovalMode = keyof typeof PROMPT_MODE_APPROVAL_REQUIREMENTS;

/**
 * Returns the required-approver kinds for a prompt mode, widened to
 * `readonly CgcMemberKind[]`. The `as const` on `PROMPT_MODE_APPROVAL_REQUIREMENTS`
 * narrows each entry's `requiredKinds` to a literal tuple type whose
 * union-over-modes intersects to `never`, breaking `.includes(kind)` at
 * call sites. This helper performs the widening once so callers get a
 * sound `.includes` without inline casts at every site. The `satisfies`
 * clause on the approval map guarantees every literal element is a valid
 * `CgcMemberKind`, so the return-type annotation is the type-safe widening.
 */
export function requiredKindsForMode(
  mode: keyof typeof PROMPT_MODE_APPROVAL_REQUIREMENTS,
): readonly CgcMemberKind[] {
  return PROMPT_MODE_APPROVAL_REQUIREMENTS[mode].requiredKinds;
}

/**
 * True when `kind` is permitted to approve `mode` per §5.5 + any per-member
 * override. `memberModesAllowed` is the CGC member's per-row override list
 * (`cgcMembers.modesAllowedJson`); when non-empty, both the kind requirement
 * AND the member-level allow-list must pass. Empty member-list = kind-default.
 */
export function canApproveMode(
  kind: CgcMemberKind,
  memberModesAllowed: readonly string[],
  mode: keyof typeof PROMPT_MODE_APPROVAL_REQUIREMENTS,
): boolean {
  if (!requiredKindsForMode(mode).includes(kind)) return false;
  if (memberModesAllowed.length === 0) return true;
  return memberModesAllowed.includes(mode);
}

export function requiresJointSignOff(
  mode: keyof typeof PROMPT_MODE_APPROVAL_REQUIREMENTS,
): boolean {
  return PROMPT_MODE_APPROVAL_REQUIREMENTS[mode].jointSignOff;
}

// =============================================================================
// Slice 23 — CGC incident-review routing (DESIGN §5.5, §9.4 #9).
//
// When a `Red_Flag_Fired` event lands in `maeve_incident_log`, the relevant
// CGC discipline must clinically review it within 24 hours Mon–Fri. The
// notification fan-out + the CGC dashboard's "unreviewed incidents" filter
// both use this map to decide which `cgc_members.kind` is responsible for
// each `triggeringCategory`:
//   - Clinical risk (self-harm / suicidality / impairment / grief markers /
//     substance / bereavement disclosure) → mental health professional +
//     GP/psychiatrist. Either kind can sign off the review; both are
//     notified so the SLA holds even if one is unavailable.
//   - `safety_concerns` → legal counsel only (workplace-safety / abuse /
//     legal-exposure category per DESIGN §5.5).
//
// `null` and unrecognised categories fall back to the broad clinical set
// so a misfire still surfaces to someone who can triage it.
// =============================================================================

// Slice 32 — classifier-set version + per-category metadata for CGC review.
//
// CLASSIFIER_SET_VERSION bumps any time the set of categories OR their
// per-category routing / description / guardrail copy changes in code.
// CGC reviews are recorded per-version in `classifier_category_reviews`;
// when this constant bumps, all prior approvals are invalidated and the
// `/admin/governance` inspector + `/cgc` dashboard surface every category
// as "Re-review required for v<new>" until each is approved against the
// new version.
//
// Bump convention: 1.0.0 is the candidate set per DESIGN §5.3.1. Patch
// bumps for copy changes (description / guardrail text); minor for
// routing or per-category gate changes; major for adding/removing
// categories. Updating this string is the trigger event for a CGC
// re-review cycle — don't bump it without expecting the cycle.
export const CLASSIFIER_SET_VERSION = '1.0.0';

// Slice 47 — per-category §10.2 dictionary set version.
//
// Mirrors `CLASSIFIER_SET_VERSION` (line 764) + `QA_RUBRIC_VERSION` (in
// `@besideu/shared/qa-rubric`). Bumps any time the per-(category, tier)
// routing or the tier semantics change; does NOT bump on copy / wording
// / phrase-list changes (those land as new D1 rows against the current
// version, the audit trail captures the wording-at-the-time). CGC
// reviews are recorded per-version in
// `traffic_light_dictionary_reviews`; when this constant bumps, all
// prior approvals are invalidated and the CGC dashboard surfaces every
// category as "Re-review required for v<new>" until each is approved
// against the new version.
//
// Bump convention: 1.0.0 is the engineering baseline + CGC additions
// per the M3 sign-off pass. Patch for phrase-list / wording changes
// (the rare case where the CGC moves the whole dictionary to a new
// baseline); minor for per-(category, tier) routing or gate changes;
// major for adding / removing a tier or per-category routing change.
// Updating this string is the trigger event for a CGC re-review cycle
// — don't bump it without expecting the cycle.
export const DICTIONARY_SET_VERSION = '1.0.0';

// =============================================================================
// Adversarial test scenario programme (PROJECT §5 hard gate #5 / DESIGN §9.4
// #9 / §10.2). The CGC authors 100+ adversarial scenarios PER Maeve mode and
// signs each mode's set off, version by version. Mirrors the versioned
// classifier-category + traffic-light-dictionary sign-off precedent.
//
// `ADVERSARIAL_SCENARIO_SET_VERSION` is the invalidation gate: every review
// row stores the version it was made against, and the inspector only counts
// reviews at the CURRENT version — so bumping this string re-opens every
// mode's sign-off. Bump convention mirrors CLASSIFIER_SET_VERSION: patch for
// scenario wording, minor for structural changes to what a scenario must
// include, major for adding/removing a mode.
// =============================================================================
export const ADVERSARIAL_SCENARIO_SET_VERSION = '1.0.0';

// The Maeve modes that require a signed-off adversarial scenario set before
// go-live. The three user-facing modes (the surfaces a hostile/distressed
// user actually reaches). Background classifiers are covered by their own
// per-category review.
export const ADVERSARIAL_SCENARIO_MODES = MAEVE_USER_MODES;
export type AdversarialScenarioMode = MaeveUserMode;

// Which CGC kind(s) may author + sign off each mode's scenario set. Mirrors
// the `requiredKinds` in `PROMPT_MODE_APPROVAL_REQUIREMENTS` for the same
// mode: the discipline that owns the mode owns its adversarial suite. A
// length > 1 entry is a JOINT sign-off (one approval per required kind).
export const CGC_KINDS_FOR_SCENARIO_MODE: Record<
  AdversarialScenarioMode,
  readonly CgcMemberKind[]
> = {
  HR_Maeve: ['legal_counsel'],
  Manager_Guidance: ['grief_counsellor'],
  Employee_Companion: ['mhp', 'gp_psychiatrist'],
};

// Per-category metadata that the CGC inspector + reviewer dashboards
// render. Description is the operator-facing summary of what the
// category indicates; guardrailCopy is the canonical short routing
// reminder (Cruse for grief, Drinkline for substance, etc.) that the
// Companion prompt's per-category response branches should incorporate.
// These constants are the source of truth in code; the CGC reviews them
// per-version via the inspector UI and signs off without an engineer
// touching the file.
export interface ClassifierCategoryMetadata {
  readonly displayName: string;
  readonly description: string;
  readonly guardrailCopy: string;
}

export const CLASSIFIER_CATEGORY_METADATA: Record<ClassifierCategory, ClassifierCategoryMetadata> =
  {
    suicidal_ideation: {
      displayName: 'Suicidal ideation',
      description:
        'User expresses thoughts of suicide — passive (wishing to be dead) through active (plan / means / timeline). Red flag triggers immediate handoff to Samaritans / SHOUT / 999 and HR Safety Contact notification.',
      guardrailCopy:
        'Crisis routing: Samaritans 116 123 (UK), SHOUT text 85258, 999 for emergency. Maeve must NOT engage on planning, methods, or timeline. Session-end + DO content wipe per §3.3.4.',
    },
    self_harm_intent: {
      displayName: 'Self-harm intent',
      description:
        'User expresses intent to self-harm (distinct from suicidal ideation). Red flag triggers safety-handoff; amber escalates aggregation.',
      guardrailCopy:
        'Crisis routing same as suicidal_ideation. Distinguished for incident-log classification + clinical review.',
    },
    severe_isolation: {
      displayName: 'Severe isolation',
      description:
        'User describes being disconnected from all support — no family, no friends, no community contact since the loss. Strong predictor of complicated grief outcomes.',
      guardrailCopy:
        "Route to Cruse Bereavement Care (0808 808 1677 UK) and the user's EAP if known. Maeve continues MI-style conversation; surface bereavement support resources at amber.",
    },
    functional_impairment: {
      displayName: 'Functional impairment',
      description:
        'User describes inability to perform daily life activities (work attendance, sleep, eating, hygiene) that persists beyond expected acute-grief window.',
      guardrailCopy:
        'Route to GP referral pathway, EAP, and Cruse. Manager mode receives a touchpoint-prep brief flagging the impairment dimension.',
    },
    prolonged_grief_indicators: {
      displayName: 'Prolonged grief indicators',
      description:
        'Markers of prolonged-grief-disorder per DSM-5-TR / ICD-11 — yearning, identity disruption, role confusion, persistent at >12 months post-loss.',
      guardrailCopy:
        'Route to Cruse and to GP for clinical assessment. Maeve does NOT diagnose; surfaces the pattern to the user as "worth talking to a professional about".',
    },
    substance_use_risk: {
      displayName: 'Substance use risk',
      description:
        'User describes alcohol or drug use as a grief-coping mechanism in escalating frequency or quantity.',
      guardrailCopy:
        'Route to Drinkline (0300 123 1110 UK) and Adfam. Manager / HR routing follows EAP. Maeve avoids advice on substances themselves.',
    },
    suicide_bereavement_disclosure: {
      displayName: 'Suicide bereavement disclosure',
      description:
        'User discloses the death was by suicide. Distinct elevated-risk category — bereaved-by-suicide carry higher own-suicide risk and complicated-grief risk.',
      guardrailCopy:
        'Route to Survivors of Bereavement by Suicide (SoBS, 0300 111 5065 UK) and Cruse. Maeve treats with extra care — avoid platitudes, validate complexity.',
    },
    safety_concerns: {
      displayName: 'Safety concerns',
      description:
        'Workplace-safety, abuse, or legal-exposure issues surfaced in conversation. Distinct from clinical categories — escalates to legal counsel.',
      guardrailCopy:
        'Route to organisational safeguarding lead and to legal counsel. Maeve does NOT provide legal or safeguarding advice; defers to human reviewers.',
    },
  };

export const CGC_KINDS_FOR_INCIDENT: Record<ClassifierCategory, readonly CgcMemberKind[]> = {
  suicidal_ideation: ['mhp', 'gp_psychiatrist'],
  self_harm_intent: ['mhp', 'gp_psychiatrist'],
  severe_isolation: ['mhp', 'gp_psychiatrist'],
  functional_impairment: ['mhp', 'gp_psychiatrist'],
  prolonged_grief_indicators: ['mhp', 'gp_psychiatrist'],
  substance_use_risk: ['mhp', 'gp_psychiatrist'],
  suicide_bereavement_disclosure: ['mhp', 'gp_psychiatrist'],
  safety_concerns: ['legal_counsel'],
};

const FALLBACK_CGC_KINDS: readonly CgcMemberKind[] = ['mhp', 'gp_psychiatrist'];

/**
 * Resolve the set of CGC member kinds responsible for reviewing an
 * incident with the given triggering category. `null` (some
 * Crisis_Resources_Surfaced events aren't category-specific) or an
 * unrecognised category falls back to the broad clinical set.
 */
export function cgcKindsForIncidentCategory(category: string | null): readonly CgcMemberKind[] {
  if (!category) return FALLBACK_CGC_KINDS;
  if ((CLASSIFIER_CATEGORIES as readonly string[]).includes(category)) {
    return CGC_KINDS_FOR_INCIDENT[category as ClassifierCategory];
  }
  return FALLBACK_CGC_KINDS;
}

// =============================================================================
// Deterministic crisis-resource notice — DESIGN §3.3.4, §5.3.1; PROJECT.md
// hard rule: "Crisis resources must stay available even if model inference
// fails."
//
// The model normally weaves region-aware crisis resources into its own `red`
// response. This constant is the DETERMINISTIC floor that does NOT depend on
// the model: it is returned to (or appended to) the user on the §5.3.1
// pre-classification short-circuit and whenever model inference fails on a
// turn whose input deterministically matched a crisis signal. That way the
// resources reach a user in crisis even during a Vertex outage.
//
// Region-aware is UK-only today — it mirrors the resources already embedded
// in the signed-off `suicidal_ideation` classifier metadata above. The
// function takes a region so call sites don't change when a per-region table
// lands. The copy is signposting (not clinical advice); a CGC legal-counsel
// review of this exact string rides with the AI-disclosure sign-off gate.
// =============================================================================
export const CRISIS_RESOURCES_NOTICE_UK =
  'If you are in immediate danger, please call 999 now. For free, confidential ' +
  'support at any time you can call Samaritans on 116 123 or text SHOUT to 85258. ' +
  'You deserve support, and help is available right now.';

export type CrisisResourceRegion = 'UK';

export function crisisResourcesNotice(_region: CrisisResourceRegion = 'UK'): string {
  return CRISIS_RESOURCES_NOTICE_UK;
}

export type BoilerplateMissingItem = 'intended_use' | 'human_oversight' | 'prohibited_use';

export type BoilerplateValidation = { ok: true } | { ok: false; missing: BoilerplateMissingItem[] };

// Slice 27 — the validator now accepts an injected boilerplate object so the
// hot read path can pass DB-active text without making this module aware of
// Drizzle. The default falls back to the code constants in this file —
// preserving the pre-Slice-27 behaviour for any caller that hasn't yet been
// wired to fetch the active rows from `system_prompts`.
export interface MaeveBoilerplate {
  /** Verbatim text the prompt must include (`prompt.includes(...)`). */
  readonly intendedUseStatement: string;
  /** Verbatim text the prompt must include (`prompt.includes(...)`). */
  readonly humanOversightClause: string;
  /**
   * Anchor list — the prompt must include AT LEAST ONE entry. Legacy
   * (constants) supplies three short phrases; the DB-loaded version
   * supplies a single canonical body in a one-element array. Both shapes
   * satisfy the same validator branch.
   */
  readonly prohibitedUseAnchors: readonly string[];
}

export const DEFAULT_MAEVE_BOILERPLATE: MaeveBoilerplate = {
  intendedUseStatement: INTENDED_USE_STATEMENT,
  humanOversightClause: HUMAN_OVERSIGHT_CLAUSE,
  prohibitedUseAnchors: PROHIBITED_USE_CASES_ANCHORS,
};

export function validateMaeveModeBoilerplate(
  promptText: string,
  boilerplate: MaeveBoilerplate = DEFAULT_MAEVE_BOILERPLATE,
): BoilerplateValidation {
  const missing: BoilerplateMissingItem[] = [];
  if (!promptText.includes(boilerplate.intendedUseStatement)) missing.push('intended_use');
  if (!promptText.includes(boilerplate.humanOversightClause)) missing.push('human_oversight');
  if (!boilerplate.prohibitedUseAnchors.some((a) => promptText.includes(a))) {
    missing.push('prohibited_use');
  }
  return missing.length === 0 ? { ok: true } : { ok: false, missing };
}
